import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { debounce, styled, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';

import { useAuthenticator } from '@aws-amplify/ui-react';
import ModalProvider from 'mui-modal-provider';

import ConfirmDialogProvider from '../../components/Dialogs/Confirm/context/ConfirmDialog.provider';
import { useAnalyticsContext } from '../../contexts/AnalyticsContext';
import usePathAttributes from '../../contexts/AnalyticsContext/usePathAttributes';
import ConversationsProvider from '../../contexts/ConversationsContext/provider';
import NotificationsProvider from '../../contexts/NotificationsContext/provider';
import { ReservationsLimitInPeriodProvider } from '../../contexts/ReservationsLimitInPeriodContext/provider';
import { VenueStatus } from '../../graphql/API';
import useBreakpoints from '../../hooks/useBreakpoints';
import { usePersistLocalStorageState } from '../../hooks/usePersistLocalStorageState';
import { fetchAccountBalance } from '../../redux/features/balance/balanceSlice';
import { fetchReservation } from '../../redux/features/reservation/reservationSlice';
import {
  fetchStripePricing,
  fetchSubscription,
  updatePremium,
} from '../../redux/features/subscription/subscriptionSlice';
import { fetchVenue } from '../../redux/features/venue/venueSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { Chat } from '../ChatSupport/Chat';
import MigrationRenewalGate from '../Dialogs/MigrationRenewal/MigrationRenewalGate';
import { ShipmentReminder } from '../ShipmentReminder/ShipmentReminder';
import Header from './Header/Header';
import LoadingAccount, { PageLoading } from './LoadingAccount';
import MobileDownloadGate from './MobileDownloadGate';
import SideBar from './SideMenu/SideMenu';
import usePath from './usePath';

const Parent = styled(Box)({ display: 'flex', flexDirection: 'column', height: '100vh' });
const Content = styled(Box)({
  display: 'flex',
  flexGrow: 1,
  overflow: 'auto',
  position: 'relative',
  minWidth: 0,
});
const Main = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  height: '100%',
  overflow: 'auto',
  // Without this a flex child refuses to shrink below its content width, so
  // any wide toolbar pushes the whole page into horizontal scroll instead of
  // letting its own children wrap or truncate.
  minWidth: 0,
  backgroundColor:
    theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
}));

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const analytic = useAnalyticsContext();
  const venue = useAppSelector((state) => state.venue.value);
  const pathAttributes = usePathAttributes();
  const location = useLocation();

  // Mirror the latest location into a ref so the auth-redirect effect can read
  // it without re-running on every navigation (which would refetch venue data).
  const locationRef = React.useRef(location);
  locationRef.current = location;

  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { shouldCollapseDrawer } = useBreakpoints();
  const { authStatus } = useAuthenticator();
  const [drawerOpen, setDrawerOpen] = usePersistLocalStorageState('side-menu-open', true);
  const { mainRef, path } = usePath();

  // The 250px drawer costs ~20% of the viewport on a 13" laptop, which is
  // exactly where the page toolbars and the wider data tables start running
  // out of room. Collapse it automatically below 1440px — the user can always
  // reopen it with the chevron at the bottom of the drawer, and that choice
  // sticks until the viewport crosses the threshold again.
  const autoCollapsed = React.useRef<boolean | null>(null);
  React.useEffect(() => {
    if (autoCollapsed.current === null) {
      // First run: only ever force the drawer closed. Forcing it *open* here
      // would throw away a collapse the user had deliberately persisted.
      if (shouldCollapseDrawer) setDrawerOpen(false);
    } else if (autoCollapsed.current !== shouldCollapseDrawer) {
      setDrawerOpen(!shouldCollapseDrawer);
    }

    autoCollapsed.current = shouldCollapseDrawer;
  }, [shouldCollapseDrawer, setDrawerOpen]);

  const debounceFetchVenue = React.useMemo(
    () =>
      debounce(() => {
        dispatch(fetchVenue());
      }, 300),
    [dispatch],
  );

  const debounceFetchOthers = React.useMemo(
    () =>
      debounce((venueID: string, premium: boolean) => {
        dispatch(fetchReservation({ venueID }));
        dispatch(fetchStripePricing());
        dispatch(updatePremium(premium));
        dispatch(fetchSubscription({ venueID: Number(venueID), status: 'active' }));
        dispatch(fetchAccountBalance({ venueID }));
      }, 300),
    [dispatch],
  );

  const isAbleLogin = React.useMemo(
    () => Boolean(venue && [VenueStatus.active, VenueStatus.inactive].includes(venue.status)),
    [venue],
  );

  React.useEffect(() => {
    if (authStatus === 'authenticated') {
      if (!venue) {
        debounceFetchVenue();
      }

      if (venue && isAbleLogin) {
        const venueID = venue.id;
        debounceFetchOthers(venueID, Boolean(venue.premium));
      }
    }

    if (authStatus === 'unauthenticated') {
      // Remember where the user was headed so Login (via useFrom) can send them
      // back there after signing in, instead of defaulting to the dashboard.
      // Also carry the original query string onto /login: a referral token
      // (e.g. ?referralInstaHandle=) arriving on /users/:id must survive the
      // bounce so the Login footer can forward it to the website sign-up.
      navigate(`/login${locationRef.current.search}`, {
        replace: true,
        state: { from: locationRef.current },
      });
    }
  }, [venue, isAbleLogin, authStatus, debounceFetchVenue, debounceFetchOthers, navigate]);

  React.useEffect(() => {
    if (pathAttributes) {
      analytic.record('SCREEN_VIEW', pathAttributes);
    }
  }, [analytic, pathAttributes]);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // The dashboard is desktop-only; on mobile, send authenticated users to the
  // native apps instead of rendering the unusable layout.
  if (isMobile && authStatus === 'authenticated') {
    return <MobileDownloadGate />;
  }

  return (
    <ModalProvider>
      <ConfirmDialogProvider>
        <CssBaseline />

        {venue && isAbleLogin ? (
          <Parent>
            <Helmet>
              <title>{path?.label ? `Beautypass - ${path.label}` : 'Beautypass'} </title>
            </Helmet>

            <Box sx={{ display: 'flex' }}>
              <Header />
            </Box>

            <ConversationsProvider>
              <NotificationsProvider>
                <ReservationsLimitInPeriodProvider>
                  <Content>
                    <SideBar drawerOpen={drawerOpen} onDrawerClick={toggleDrawer} />

                    <Main component='main' ref={mainRef}>
                      <React.Suspense fallback={<PageLoading />}>
                        <Outlet />
                      </React.Suspense>
                    </Main>
                    {location.pathname !== '/chats' && <Chat />}
                    <ShipmentReminder />
                    <MigrationRenewalGate />
                  </Content>
                </ReservationsLimitInPeriodProvider>
              </NotificationsProvider>
            </ConversationsProvider>
          </Parent>
        ) : (
          <LoadingAccount venue={venue} />
        )}
      </ConfirmDialogProvider>
    </ModalProvider>
  );
};

export default Layout;
